import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { UPGRADE_STATUS } from '../../constants/upgrades';
import { todayIso, addDaysIso } from '../../utils/upgradesApi';

const DOW = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const MAX_CHIPS = 3;
const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// Days of the month grid (Monday first), including the lead-in and tail days of the neighbouring months.
function monthDays(year, month) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = addDaysIso(iso(year, month, 1), -offset);
  const count = Math.ceil((offset + new Date(year, month + 1, 0).getDate()) / 7) * 7;
  return Array.from({ length: count }, (_, i) => addDaysIso(start, i));
}

// Month view of planned trips. A multi-day job appears on every day of its range.
const UpgradeCalendar = ({ jobs, onOpen }) => {
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const today = todayIso();
  const days = useMemo(() => monthDays(cursor.year, cursor.month), [cursor]);
  const scheduled = useMemo(() => jobs.filter((j) => j.planned_start && j.status !== 'zruseno'), [jobs]);

  const jobsOn = (day) => scheduled.filter((j) => j.planned_start <= day && day <= (j.planned_end || j.planned_start));
  const shift = (delta) => setCursor(({ year, month }) => {
    const d = new Date(year, month + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const title = new Date(cursor.year, cursor.month, 1).toLocaleDateString('cs', { month: 'long', year: 'numeric' });

  return (
    <section aria-label="Kalendář výjezdů">
      <div className="upg-cal__head">
        <button type="button" className="dlv-iconbtn" onClick={() => shift(-1)} aria-label="Předchozí měsíc"><ChevronLeft size={16} /></button>
        <h2 className="upg-cal__title" aria-live="polite">{title}</h2>
        <button type="button" className="dlv-iconbtn" onClick={() => shift(1)} aria-label="Další měsíc"><ChevronRight size={16} /></button>
        <button type="button" className="dlv-btn upg-sm" onClick={() => setCursor({ year: now.getFullYear(), month: now.getMonth() })}>Dnes</button>
      </div>
      <div className="upg-cal__grid" role="grid" aria-label={title}>
        {DOW.map((d) => <div key={d} className="upg-cal__dow" role="columnheader">{d}</div>)}
        {days.map((day) => {
          const list = jobsOn(day);
          const inMonth = Number(day.slice(5, 7)) - 1 === cursor.month;
          return (
            <div key={day} role="gridcell" className={`upg-cal__day ${inMonth ? '' : 'upg-cal__day--out'} ${day === today ? 'upg-cal__day--today' : ''}`}>
              <span className="upg-cal__num">{Number(day.slice(8))}</span>
              {list.slice(0, MAX_CHIPS).map((j) => {
                const s = UPGRADE_STATUS[j.status];
                const crew = j.crew.map((c) => c.name).join(', ');
                return (
                  <button key={j.id} type="button" className="upg-cal__chip" style={{ background: s.bg, color: s.color }} onClick={() => onOpen(j.id)}
                    title={`${j.number} · ${j.customer_name}${crew ? ` · jede: ${crew}` : ''}`}>
                    {j.arrival_time && j.planned_start === day ? `${j.arrival_time} ` : ''}{j.customer_name}
                  </button>
                );
              })}
              {list.length > MAX_CHIPS && <span className="upg-cal__more">+ {list.length - MAX_CHIPS} další</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default UpgradeCalendar;
